import React, { useReducer } from 'react'
import { DropzoneProps } from 'react-dropzone'

import { Box, Button, ProgressBar, Text } from '@factorialco/deprecated-design-system'

import DocumentType from 'modules/core/components/DocumentType'
import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { UploadStatus, useDirectUpload } from 'modules/core/hooks/useDirectUpload'
import { i18nDateToString } from 'modules/core/lib/dates'
import { acceptedContentTypes } from 'modules/core/lib/fileFormats'
import i18n from 'modules/core/lib/i18n'

import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'

import { formatFileSize } from '../utils'
import { FileSubmissionData, UploadValue } from './types'

type FileTypeCategory = 'document' | 'image' | 'video' | 'text'

export const NewFileModal = ({
  onClose,
  onSubmit,
  resourceType,
  resourceId,
  title,
  noTitleField: noTitle,
  fileTypes = ['document', 'image', 'video', 'text'],
  description,
}: {
  noTitleField?: boolean
  onClose: () => void
  onSubmit: (data: FileSubmissionData) => Promise<unknown>
  description: string
  title: string
  resourceId?: string
  resourceType: 'Trainings::Training' | 'Trainings::TrainingClass' | 'Trainings::TrainingMembership'
  fileTypes?: FileTypeCategory[] // Categories of files to accept
}) => {
  const { access } = useCurrentSession()
  const [state, dispatch] = useReducer(formReducer, initialState)
  const {
    upload: directUploadFile,
    status: uploadStatus,
    progress,
    cancelUpload,
  } = useDirectUpload({
    resourceType,
    resourceId,
  })

  return (
    <Modal
      illustration="upload"
      description={description}
      title={title}
      onClose={() => {
        if (state.status === 'uploading' && state.upload?.type === 'signedId') {
          cancelUpload()
        }
        onClose()
      }}
      onSubmit={async () => {
        if (!state.upload) throw new Error('No file uploaded')
        await onSubmit({
          ...state.upload,
          title: state.title,
          authorId: access.id,
        })
      }}
      footer={
        <Button disabled={!state.upload || state.status === 'uploading'} submit>
          {i18n.t('trainings.trainings_files_modal.save')}
        </Button>
      }
    >
      <Box gap="s16">
        {!noTitle && (
          <TextField
            value={state.title}
            onChange={(value) => dispatch({ type: 'SET_TITLE', payload: value })}
            label={i18n.t('trainings.trainings_files_modal.field_title')}
          />
        )}
        <FileUploadSection
          state={state}
          uploadStatus={uploadStatus}
          progress={progress}
          dispatch={dispatch}
          directUploadFile={directUploadFile}
          cancelUpload={cancelUpload}
          fileTypes={fileTypes}
        />
      </Box>
    </Modal>
  )
}

interface FormError {
  error: Error | null
  message: string | null
}

interface FormState {
  upload?: UploadValue
  title?: string
  status: 'idle' | 'uploading' | 'uploaded' | 'error'
  error: FormError
}

type FormAction =
  | { type: 'START_DIRECT_UPLOAD'; payload: File }
  | { type: 'SET_UPLOAD_COMPLETE'; payload: UploadValue }
  | { type: 'CLEAR_UPLOAD' }
  | { type: 'SET_TITLE'; payload: string | undefined }
  | { type: 'SET_ERROR'; payload: { error?: Error; message: string } }

const CLEAR_ERROR = { error: null, message: null }

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SET_ERROR':
      return {
        ...state,
        status: 'error',
        upload: undefined,
        error: {
          error: action.payload.error ?? null,
          message: action.payload.message,
        },
      }
    case 'START_DIRECT_UPLOAD':
      return {
        ...state,
        upload: { type: 'signedId', file: action.payload },
        status: 'uploading',
        error: CLEAR_ERROR,
      }
    case 'SET_UPLOAD_COMPLETE':
      return {
        ...state,
        upload: action.payload,
        status: 'uploaded',
        error: CLEAR_ERROR,
      }
    case 'CLEAR_UPLOAD':
      return {
        ...state,
        upload: undefined,
        status: 'idle',
        error: CLEAR_ERROR,
      }
    case 'SET_TITLE':
      return { ...state, title: action.payload }
    default:
      return state
  }
}

const initialState: FormState = {
  upload: undefined,
  title: undefined,
  status: 'idle',
  error: CLEAR_ERROR,
}

type FileUploadSectionProps = {
  state: FormState
  uploadStatus: UploadStatus | 'error' | 'aborted'
  progress: number
  dispatch: React.Dispatch<FormAction>
  directUploadFile: (
    file: File
  ) => Promise<{ type: 'success'; signedId: string } | { type: 'aborted' }>
  cancelUpload: () => void
  fileTypes: FileTypeCategory[]
}

function FileUploadSection({
  state,
  uploadStatus,
  progress,
  dispatch,
  directUploadFile,
  cancelUpload,
  fileTypes,
}: FileUploadSectionProps) {
  if (state.status === 'idle' || state.status === 'error') {
    return (
      <>
        <DropzoneBox
          onDrop={async (files: File[]) => {
            dispatch({ type: 'CLEAR_UPLOAD' }) // This will clear the error state too
            const validationResult = validateFile(files[0])
            if (!validationResult.isValid) {
              dispatch({
                type: 'SET_ERROR',
                payload: {
                  error: new Error(validationResult.error),
                  message: validationResult.error,
                },
              })
              return
            }

            const file = validationResult.file

            if (validationResult.file.type.startsWith('video/')) {
              try {
                dispatch({ type: 'START_DIRECT_UPLOAD', payload: file })
                const uploadResult = await directUploadFile(file)
                switch (uploadResult.type) {
                  case 'aborted':
                    dispatch({ type: 'CLEAR_UPLOAD' })
                    break
                  case 'success':
                    dispatch({
                      type: 'SET_UPLOAD_COMPLETE',
                      payload: { type: 'signedId', file, signedId: uploadResult.signedId },
                    })
                    break
                  default:
                    throw new Error('Unknown upload result')
                }
              } catch (error: unknown) {
                if (error instanceof Error) {
                  dispatch({
                    type: 'SET_ERROR',
                    payload: {
                      error,
                      message: i18n.t('trainings.trainings_files_modal.failed_to_upload'),
                    },
                  })
                } else {
                  dispatch({
                    type: 'SET_ERROR',
                    payload: {
                      error: new Error('An unknown upload error occurred'),
                      message: i18n.t('trainings.trainings_files_modal.unexpected_error'),
                    },
                  })
                }
              }
            } else {
              dispatch({ type: 'SET_UPLOAD_COMPLETE', payload: { type: 'file', file } })
            }
          }}
          multiple={false}
          message={i18n.t('documents.drop_here')}
          description={
            fileTypes.includes('video')
              ? i18n.t('trainings.trainings_files_modal.size_description_video')
              : i18n.t('trainings.trainings_files_modal.size_description_all')
          }
          accept={toDropzoneFormat(fileTypes)}
          error={state.status === 'error'}
          iconName="file-upload"
        />
        {state.error.message && (
          <Text color="radicalRed1000" size="200">
            {state.error.message}
          </Text>
        )}
      </>
    )
  }

  if (state.upload) {
    return (
      <Box
        border={{ all: { color: 'grey400', style: 'solid', width: 's1' } }}
        borderRadius={{
          all: 'abs008',
        }}
        gap="s8"
        paddingY="s12"
        paddingX="s16"
      >
        <Box flexDirection="row" gap="s16" alignItems="center" justifyContent="spaceBetween">
          <DocumentType mimetype={state.upload.file.type} />
          <Box gap="s2" flexGrow>
            <Text weight="medium" size="300">
              {state.upload.file.name}
            </Text>
            {uploadStatus === 'processing' ? (
              <Text size="200">{i18n.t('trainings.trainings_files_modal.processing')}</Text>
            ) : state.status === 'uploading' && state.upload.type === 'signedId' && progress < 1 ? (
              <ProgressBar
                barColor="purple200"
                progressColor="purple1000"
                percentage={Math.max(Math.round(progress * 100), 1)}
                amount={progress}
                total={1}
              />
            ) : state.status === 'uploaded' ? (
              <Text size="200">
                {`${i18nDateToString(
                  new Date(state.upload.file.lastModified),
                  'ui_short_with_time'
                )} ${formatFileSize(state.upload.file.size)}`}
              </Text>
            ) : null}
          </Box>
          <Button
            type="secondary"
            onClick={() => {
              if (state.status === 'uploading' && state.upload?.type === 'signedId') {
                cancelUpload()
              }
              dispatch({ type: 'CLEAR_UPLOAD' })
            }}
            iconName="delete"
          />
        </Box>
      </Box>
    )
  }
  return null
}

function toDropzoneFormat(categories: FileTypeCategory[]): NonNullable<DropzoneProps['accept']> {
  const FILE_TYPES_BY_CATEGORY: Record<FileTypeCategory, string[]> = {
    document: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ],
    image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
    // Only web video formats are supported
    video: ['video/mp4', 'video/webm'],
    text: ['text/plain', 'text/csv', 'text/markdown'],
  }
  const allTypes = categories.flatMap((category) => FILE_TYPES_BY_CATEGORY[category])
  return acceptedContentTypes(allTypes)
}

const MAX_VIDEO_SIZE = 2 * 1024 * 1024 * 1024 // 2GB in bytes
const MAX_FILE_SIZE = 200 * 1024 * 1024 // 200MB in bytes
type ValidationResult = { isValid: true; file: File } | { isValid: false; error: string }
function validateFile(file: File | undefined): ValidationResult {
  if (!file) throw new Error('No file dropped')

  const maxAllowedSize = file.type.startsWith('video/') ? MAX_VIDEO_SIZE : MAX_FILE_SIZE
  if (file.size > maxAllowedSize) {
    return {
      isValid: false,
      error: i18n.t('trainings.trainings_files_modal.too_large', {
        maxAllowedSize: formatFileSize(maxAllowedSize),
      }),
    }
  }

  return { isValid: true, file }
}
